<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiOptionList from '@/components/ui/UiOptionList.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import ViewMyAppointmentDialog from '@/components/Dialogs/Views/ViewMyAppointmentDialog.vue';
import ViewMyChildrenAttendanceDialog from '@/components/Dialogs/Views/ViewMyChildrenAttendanceDialog.vue';
import ViewMyChildrenClassRecordDialog from '@/components/Dialogs/Views/ViewMyChildrenClassRecordDialog.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { titleCase } from '@/utils/format';

useSessionGuard();

// A parent's linked children, with attendance, report card and
// appointment requests for each.
const store = useStore();
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/parent-records/getMyChildrenList/${year.value}`);
  return (rows || []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch(year, load, { immediate: true });

const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Grade level', key: 'grade_level' },
  { title: 'Section', key: 'room_section' },
  { title: 'Adviser', key: 'adviser_name', hideable: true },
];

// One dialog open at a time, for the chosen child.
const selected = shallowRef(null);
const openDialog = shallowRef(null); // 'attendance' | 'report' | 'appointment'
const dialogModel = (name) =>
  computed({
    get: () => openDialog.value === name,
    set: (value) => (openDialog.value = value ? name : null),
  });
const attendanceOpen = dialogModel('attendance');
const reportOpen = dialogModel('report');
const appointmentOpen = dialogModel('appointment');
const appointmentTab = shallowRef('new');
function show(name, child, tab = 'new') {
  selected.value = child;
  appointmentTab.value = tab;
  openDialog.value = name;
}

// Notifications link here with the child and what to open:
//   ?attendance=<id>   absent/late alerts
//   ?report=<id>       grade alerts and early warnings
//   ?appointments=<id> a teacher's reply (opens My requests)
// The dialog opens once the list has loaded; the query is then dropped so
// going back or refreshing doesn't open it again.
const LINKS = { attendance: 'attendance', report: 'report', appointments: 'appointment' };
const route = useRoute();
const router = useRouter();
watch(
  [() => route.query, items],
  ([query]) => {
    const key = Object.keys(LINKS).find((k) => query[k]);
    if (!key) return;
    const child = items.value.find((c) => String(c.id) === String(query[key]));
    if (!child) return;
    show(LINKS[key], child, key === 'appointments' ? 'list' : 'new');
    const rest = { ...query };
    delete rest[key];
    router.replace({ query: rest });
  },
  { immediate: true },
);

const rowActions = (child) => [
  { title: 'Attendance', icon: 'mdi-calendar-check-outline', onClick: () => show('attendance', child) },
  { title: 'Report card', icon: 'mdi-file-chart-outline', onClick: () => show('report', child) },
  { title: 'Request appointment', icon: 'mdi-calendar-account-outline', onClick: () => show('appointment', child) },
  { title: 'Unlink', icon: 'mdi-link-variant-off', destructive: true, onClick: () => unlink(child) },
];

// Remove a child from this account, e.g. one linked by mistake. Their
// records stay at school; this account just stops seeing them.
const { confirm } = useConfirm();
async function unlink(child) {
  const ok = await confirm({
    title: `Unlink ${child.name}?`,
    description:
      "You'll no longer see their grades, attendance or report card, or get updates about them. Their school records aren't affected, and you can add them again with their LRN.",
    confirmText: 'Unlink',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/parent-records/children/${child.id}`), { success: `${child.name} was unlinked.`, error: 'Could not unlink this child.' }))) {
    load();
  }
}

// Link a child: search by LRN, pick the match, save.
const addOpen = shallowRef(false);
const lrn = shallowRef('');
const results = shallowRef([]);
const searched = shallowRef(false);
const searching = shallowRef(false);
const searchError = shallowRef('');
const studentId = shallowRef(null);
const { busy, run } = useAsyncAction();

function openAdd() {
  lrn.value = '';
  results.value = [];
  searched.value = false;
  searchError.value = '';
  studentId.value = null;
  addOpen.value = true;
}

async function search() {
  const value = lrn.value.trim();
  if (!value) return;
  searching.value = true;
  searchError.value = '';
  studentId.value = null;
  try {
    const rows = await apiRequest('get', `/parent-records/searchStudentData/${encodeURIComponent(value)}`);
    results.value = (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, name: titleCase(r.name) }));
  } catch (e) {
    results.value = [];
    searchError.value = errorMessage(e, 'Could not search right now.');
  } finally {
    searched.value = true;
    searching.value = false;
  }
}

async function link() {
  const ok = await run(
    () =>
      apiRequest('post', '/parent-records/', {
        school_yearID: year.value,
        studentID: studentId.value,
      }),
    { success: 'Child added.', error: 'Could not add the child.' },
  );
  if (ok) {
    addOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="My children"
    description="Your children enrolled this school year."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search"
    :search-fields="['lrnNo']"
    columns-key="my-children"
    add-label="Add child"
    empty-text="No children linked yet. Add one with their LRN."
    row-label="name"
    :row-actions="rowActions"
    @add="openAdd"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" :subtitle="item.lrnNo ? `LRN ${item.lrnNo}` : ''" />
    </template>
    <template #[`item.room_section`]="{ item }">{{ item.room_section || '—' }}</template>
    <template #[`item.adviser_name`]="{ item }">{{ item.adviser_name || '—' }}</template>

    <template #card="{ item }">
      <div class="child-card">
        <UiPerson :name="item.name" :subtitle="item.lrnNo ? `LRN ${item.lrnNo}` : ''" :size="40" />
        <UiDetailList
          :items="[
            { label: 'Grade level', value: item.grade_level },
            { label: 'Section', value: item.room_section },
            { label: 'Adviser', value: item.adviser_name, span: 2 },
          ]"
        />
        <div class="child-card__actions">
          <v-btn variant="outlined" size="small" prepend-icon="mdi-calendar-check-outline" @click="show('attendance', item)">
            Attendance
          </v-btn>
          <v-btn variant="outlined" size="small" prepend-icon="mdi-file-chart-outline" @click="show('report', item)">
            Report card
          </v-btn>
        </div>
      </div>
    </template>
  </UiTableCard>

  <UiDialog
    v-model="addOpen"
    title="Add a child"
    description="Search by your child's Learner Reference Number (LRN)."
  >
    <div class="form-stack">
      <form class="d-flex ga-2" @submit.prevent="search">
        <v-text-field v-model="lrn" placeholder="LRN" prepend-inner-icon="mdi-magnify" hide-details autofocus />
        <v-btn type="submit" variant="outlined" :loading="searching" :disabled="!lrn.trim()">Search</v-btn>
      </form>

      <v-alert v-if="searchError" type="error" variant="tonal" density="compact">{{ searchError }}</v-alert>
      <UiOptionList v-else-if="results.length" v-model="studentId" :items="results" item-subtitle="grade_level" />
      <UiEmptyState
        v-else-if="searched"
        icon="mdi-account-search-outline"
        title="No student found"
        description="Check the LRN and try again."
      />
    </div>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :disabled="!studentId" :loading="busy" @click="link">Add child</v-btn>
    </template>
  </UiDialog>

  <ViewMyChildrenAttendanceDialog v-model="attendanceOpen" :child="selected" />
  <ViewMyChildrenClassRecordDialog v-model="reportOpen" :child="selected" />
  <ViewMyAppointmentDialog v-model="appointmentOpen" :child="selected" :initial-tab="appointmentTab" />
</template>

<style scoped>
.child-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* Room for the card's ⋯ menu in the top-right corner. */
.child-card > :first-child {
  padding-right: 36px;
}
.child-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
